import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { ArrowRight, Play, BookOpen, Award, Clock, CheckCircle, X } from 'lucide-react';

const TALLY_FORM_URL = "https://tally.so/r/XxYvrz";

export default function LibraryPage() {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const featuresRef = useRef(null);
  const isFeaturesInView = useInView(featuresRef, { once: true, margin: "-100px" });

  const features = [
    { icon: BookOpen, title: "50+ Frameworks", description: "Proven methodologies for peak performance" },
    { icon: Play, title: "Video Lessons", description: "Learn directly from Shayamal" },
    { icon: Clock, title: "Self-Paced", description: "Learn at your own pace" },
    { icon: Award, title: "Certificates", description: "Completion certificates for each module" },
  ];

  const modules = [
    "Signal Over Noise Framework",
    "The Peak Triad Blueprint",
    "Recovery Architecture",
    "Mental Resilience Toolkit",
    "Leadership Performance System",
    "Team Performance Dynamics",
  ];

  return (
    <main data-testid="library-page">
      {/* Hero */}
      <section className="pt-24 lg:pt-32 pb-16 lg:pb-20 bg-[#F4F1EC]">
        <div className="container-peak">
          <div className="max-w-3xl mx-auto text-center px-4 sm:px-0">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="section-tag block mb-4 lg:mb-6">Peak 9 Library</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-light leading-[1.1] mb-4 lg:mb-6 text-[#1F2A44]">
                Your complete toolkit for{' '}
                <em className="not-italic text-[#D97904]">sustainable peak performance</em>
              </h1>
              <p className="text-[#5A6B7A] text-base lg:text-lg mb-8 lg:mb-10 max-w-2xl mx-auto">
                Access the same frameworks, tools, and insights used by Olympic athletes, Fortune 500 leaders, and high-performing teams worldwide.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => setIsFormOpen(true)}
                  className="btn-primary inline-flex items-center justify-center gap-3"
                >
                  Join the Waiting List
                  <ArrowRight size={16} />
                </button>
                <a href="#preview" className="btn-ghost inline-flex items-center justify-center gap-3">
                  <Play size={16} />
                  Watch Preview
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section ref={featuresRef} className="py-16 lg:py-20 bg-white">
        <div className="container-peak">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={isFeaturesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-12 h-12 lg:w-14 lg:h-14 mx-auto flex items-center justify-center border border-[#E6E1DA] mb-3 lg:mb-4">
                  <feature.icon className="w-5 h-5 lg:w-6 lg:h-6 text-[#D97904]" />
                </div>
                <h3 className="font-['Syne'] text-xs lg:text-sm uppercase tracking-wider text-[#1F2A44] mb-1 lg:mb-2">
                  {feature.title}
                </h3>
                <p className="text-[#5A6B7A] text-xs lg:text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Inside */}
      <section className="py-16 lg:py-24 bg-[#F4F1EC]">
        <div className="container-peak">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center max-w-6xl mx-auto">
            <div>
              <span className="section-tag block mb-4 lg:mb-6">What's Inside</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light leading-[1.15] mb-6 lg:mb-8 text-[#1F2A44]">
                Everything you need to{' '}
                <em className="not-italic text-[#D97904]">transform</em> your performance
              </h2>
              <div className="space-y-3 lg:space-y-4">
                {modules.map((module, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 lg:w-5 lg:h-5 text-[#588C8C] flex-shrink-0" />
                    <span className="text-[#1F2A44] text-sm lg:text-base">{module}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-[#1F2A44] p-6 lg:p-8 text-center" id="waitlist">
              <span className="text-[#D97904] font-['Syne'] text-xs uppercase tracking-wider">
                Coming Soon
              </span>
              <h3 className="text-2xl lg:text-3xl font-['Cormorant_Garamond'] text-[#F4F1EC] my-4 lg:my-6">
                Be the first to know
              </h3>
              <p className="text-[#F4F1EC]/70 text-sm mb-6 lg:mb-8">
                Join our waitlist to get early access and exclusive launch pricing.
              </p>
              <button 
                onClick={() => setIsFormOpen(true)}
                className="btn-primary w-full justify-center inline-flex items-center gap-3"
              >
                Join the Waiting List
                <ArrowRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-[#1F2A44]">
        <div className="container-peak text-center px-4 sm:px-0">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-[#F4F1EC] mb-4 lg:mb-6">
            Ready to unlock your potential?
          </h2>
          <p className="text-[#F4F1EC]/70 text-sm lg:text-base mb-6 lg:mb-8 max-w-xl mx-auto">
            Join thousands of high performers who have transformed their approach to success.
          </p>
          <button 
            onClick={() => setIsFormOpen(true)}
            className="btn-primary inline-flex items-center gap-3"
          >
            Join the Waiting List
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* Tally Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-white w-full max-w-2xl h-[80vh] rounded-none shadow-2xl relative flex flex-col"
          >
            <button
              onClick={() => setIsFormOpen(false)}
              className="absolute top-4 right-4 text-[#5A6B7A] hover:text-[#1F2A44] transition-colors z-10 bg-white rounded-full p-1"
            >
              <X size={24} />
            </button>
            <div className="flex-1 overflow-hidden">
              <iframe
                src={TALLY_FORM_URL}
                width="100%"
                height="100%"
                frameBorder="0"
                marginHeight="0"
                marginWidth="0"
                title="Join the Waiting List"
                style={{ border: 'none' }}
              />
            </div>
          </motion.div>
        </div>
      )}
    </main>
  );
}
